import { MiniGameBase } from './base.js';

/**
 * "Group Them" hierarchical mini-game
 * Player sorts items into correct categories/buckets
 */
export class GroupThemGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.playerGroups = {};
        this.completed = false;
        this.draggedItem = null;

        // Initialize empty player groups
        Object.keys(this.data.groups).forEach(groupName => {
            this.playerGroups[groupName] = [];
        });
    }

    /**
     * Render the grouping UI
     */
    render(container) {
        super.render(container);

        const ungroupedItems = this.getUngroupedItems();

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Sort these items:'}</div>
                <div class="grouping-container">
                    <div class="items-pool" data-group="pool">
                        ${ungroupedItems.map(item => `
                            <div class="grouping-item" draggable="true" data-item="${item}">
                                ${item}
                            </div>
                        `).join('')}
                    </div>
                    <div class="group-buckets">
                        ${Object.keys(this.data.groups).map(groupName => `
                            <div class="group-bucket" data-group="${groupName}">
                                <div class="bucket-header">${groupName}</div>
                                <div class="bucket-items">
                                    ${this.playerGroups[groupName].map(item => `
                                        <div class="grouping-item" draggable="true" data-item="${item}">
                                            ${item}
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                </div>
                <div class="button-row">
                    <button class="submit-button">Check Grouping</button>
                </div>
                <div class="feedback-message" style="display: none;"></div>
            </div>
        `;

        // Add skip button before submit button
        const buttonRow = container.querySelector('.button-row');
        const submitBtn = buttonRow.querySelector('.submit-button');
        const skipBtn = this.createSkipButton();
        buttonRow.insertBefore(skipBtn, submitBtn);

        this.attachEventListeners();
    }

    /**
     * Get items not yet assigned to a group
     */
    getUngroupedItems() {
        const grouped = Object.values(this.playerGroups).flat();
        return this.data.items.filter(item => !grouped.includes(item));
    }

    /**
     * Attach drag-and-drop listeners
     */
    attachEventListeners() {
        const items = this.container.querySelectorAll('.grouping-item');
        const buckets = this.container.querySelectorAll('[data-group]');
        const submitButton = this.container.querySelector('.submit-button');

        // Drag events on items (desktop)
        items.forEach(item => {
            item.addEventListener('dragstart', (e) => this.handleDragStart(e));
            item.addEventListener('dragend', (e) => this.handleDragEnd(e));

            // Touch support (mobile)
            const itemName = item.getAttribute('data-item');
            this.setupTouchDragForGrouping(item, itemName);

            // Attach hint if this item matches a word in category
            this.attachHintIfExists(item, itemName);
        });

        // Drop events on buckets
        buckets.forEach(bucket => {
            bucket.addEventListener('dragover', (e) => this.handleDragOver(e));
            bucket.addEventListener('drop', (e) => this.handleDrop(e));
            bucket.addEventListener('dragleave', (e) => this.handleDragLeave(e));
        });

        // Submit button
        submitButton.addEventListener('click', () => this.handleSubmit());
    }

    /**
     * Set up touch drag for grouping items
     */
    setupTouchDragForGrouping(element, itemName) {
        element.addEventListener('touchstart', (e) => {
            this.handleGroupTouchStart(e, element, itemName);
        }, { passive: false });

        element.addEventListener('touchmove', (e) => {
            this.handleGroupTouchMove(e);
        }, { passive: false });

        element.addEventListener('touchend', (e) => {
            this.handleGroupTouchEnd(e, itemName);
        }, { passive: false });
    }

    /**
     * Handle touch start for grouping
     */
    handleGroupTouchStart(e, element, itemName) {
        const touch = e.touches[0];
        const rect = element.getBoundingClientRect();

        this.touchDragState = {
            element: element,
            clone: null,
            itemName: itemName,
            startX: touch.clientX,
            startY: touch.clientY,
            offsetX: touch.clientX - rect.left,
            offsetY: touch.clientY - rect.top
        };

        this.touchDragState.timeout = setTimeout(() => {
            const clone = element.cloneNode(true);
            clone.classList.add('touch-dragging');
            clone.style.cssText = `
                position: fixed;
                left: ${rect.left}px;
                top: ${rect.top}px;
                width: ${rect.width}px;
                z-index: 10000;
                pointer-events: none;
                opacity: 0.9;
                transform: scale(1.05);
                box-shadow: 0 8px 20px rgba(0,0,0,0.3);
            `;
            document.body.appendChild(clone);
            this.touchDragState.clone = clone;
            element.classList.add('dragging');
            this.playSelectSound();
        }, 150);
    }

    /**
     * Handle touch move for grouping
     */
    handleGroupTouchMove(e) {
        if (!this.touchDragState.clone) {
            if (this.touchDragState.timeout) {
                clearTimeout(this.touchDragState.timeout);
                this.touchDragState.timeout = null;
            }
            return;
        }

        e.preventDefault();
        const touch = e.touches[0];

        const x = touch.clientX - this.touchDragState.offsetX;
        const y = touch.clientY - this.touchDragState.offsetY;
        this.touchDragState.clone.style.left = `${x}px`;
        this.touchDragState.clone.style.top = `${y}px`;

        // Highlight drop target
        this.touchDragState.clone.style.display = 'none';
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        this.touchDragState.clone.style.display = '';

        this.container.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));

        if (elementBelow) {
            const bucket = elementBelow.closest('[data-group]');
            if (bucket) {
                bucket.classList.add('drag-over');
            }
        }
    }

    /**
     * Handle touch end for grouping
     */
    handleGroupTouchEnd(e, itemName) {
        if (this.touchDragState.timeout) {
            clearTimeout(this.touchDragState.timeout);
            this.touchDragState.timeout = null;
        }

        const clone = this.touchDragState.clone;
        const element = this.touchDragState.element;

        if (!clone) return;

        const touch = e.changedTouches[0];
        clone.style.display = 'none';
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        clone.style.display = '';

        let targetGroup = null;
        if (elementBelow) {
            const bucket = elementBelow.closest('[data-group]');
            if (bucket) {
                targetGroup = bucket.getAttribute('data-group');
            }
        }

        // Clean up
        clone.remove();
        element.classList.remove('dragging');
        this.container.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));

        this.touchDragState = { element: null, clone: null };

        // Perform the grouping
        if (targetGroup !== null) {
            // Remove from all groups
            Object.keys(this.playerGroups).forEach(groupName => {
                this.playerGroups[groupName] = this.playerGroups[groupName].filter(
                    item => item !== itemName
                );
            });

            // Add to target group (unless it's the pool)
            if (targetGroup !== 'pool') {
                this.playerGroups[targetGroup].push(itemName);
            }

            // Re-render
            this.render(this.container);
        }
    }

    /**
     * Handle drag start
     */
    handleDragStart(e) {
        this.playSelectSound();
        this.draggedItem = e.target.getAttribute('data-item');
        e.target.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
    }

    /**
     * Handle drag end
     */
    handleDragEnd(e) {
        e.target.classList.remove('dragging');
        this.draggedItem = null;
    }

    /**
     * Handle drag over bucket
     */
    handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const bucket = e.currentTarget.closest('[data-group]');
        if (bucket) {
            bucket.classList.add('drag-over');
        }
    }

    /**
     * Handle drag leave bucket
     */
    handleDragLeave(e) {
        const bucket = e.currentTarget.closest('[data-group]');
        if (bucket && !bucket.contains(e.relatedTarget)) {
            bucket.classList.remove('drag-over');
        }
    }

    /**
     * Handle drop on bucket
     */
    handleDrop(e) {
        e.preventDefault();

        const bucket = e.currentTarget.closest('[data-group]');
        bucket.classList.remove('drag-over');

        const targetGroup = bucket.getAttribute('data-group');

        if (!this.draggedItem) return;

        // Remove from all groups
        Object.keys(this.playerGroups).forEach(groupName => {
            this.playerGroups[groupName] = this.playerGroups[groupName].filter(
                item => item !== this.draggedItem
            );
        });

        // Add to target group (unless it's the pool)
        if (targetGroup !== 'pool') {
            this.playerGroups[targetGroup].push(this.draggedItem);
        }

        // Re-render
        this.render(this.container);
    }

    /**
     * Handle submit button
     */
    handleSubmit() {
        const isCorrect = this.checkAnswer();
        const feedback = this.container.querySelector('.feedback-message');
        const submitButton = this.container.querySelector('.submit-button');

        feedback.style.display = 'block';

        if (isCorrect) {
            this.playMatchSound();
            feedback.className = 'feedback-message success';
            feedback.textContent = 'All Sorted!';
            this.completed = true;
            submitButton.disabled = true;

            // Mark items as correct
            const items = this.container.querySelectorAll('.grouping-item');
            items.forEach(item => {
                item.classList.add('correct');
                item.draggable = false;
            });

            setTimeout(() => this.showCelebration(), 1000);
        } else {
            this.playErrorSound();
            feedback.className = 'feedback-message error';
            feedback.textContent = 'Not quite right. Keep sorting!';

            setTimeout(() => {
                feedback.style.display = 'none';
            }, 2000);
        }

        return { action: isCorrect ? 'correct' : 'incorrect' };
    }

    /**
     * Check if player grouping matches correct grouping
     */
    checkAnswer() {
        // Check that all items are grouped (none in pool)
        if (this.getUngroupedItems().length > 0) {
            return false;
        }

        // Check that each group matches
        return Object.keys(this.data.groups).every(groupName => {
            const correct = this.data.groups[groupName].sort();
            const player = this.playerGroups[groupName].sort();

            return correct.length === player.length &&
                   correct.every((item, idx) => item === player[idx]);
        });
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        return this.completed;
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('groups' in selection) {
            this.playerGroups = selection.groups;
            this.render(this.container);
            return { action: 'grouped' };
        }
        return { action: 'none' };
    }
}
